import {
  Button,
  Label,
  Input,
  Textarea,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Alert, AlertDescription
} from '@creanizer/ui'
import { Save, Trash2, Download, Upload } from 'lucide-react'
import { useState } from 'react'
import type { ManualCharacterInput } from '@creanizer/types'

export interface UserPreset {
  id: string
  name: string
  description?: string
  settings: ManualCharacterInput
  createdAt: string
  updatedAt: string
}

interface PresetManagerProps {
  currentSettings: ManualCharacterInput
  onLoadPreset: (settings: ManualCharacterInput) => void
}

export function PresetManager({ currentSettings, onLoadPreset }: PresetManagerProps) {
  const [presets, setPresets] = useState<UserPreset[]>(() => {
    const saved = localStorage.getItem('imagePromptPresets')
    return saved ? JSON.parse(saved) : []
  })
  const [showSaveForm, setShowSaveForm] = useState(false)
  const [presetName, setPresetName] = useState('')
  const [presetDescription, setPresetDescription] = useState('')
  const [selectedPresetId, setSelectedPresetId] = useState('')
  const [importError, setImportError] = useState<string | null>(null)
  
  const savePresets = (newPresets: UserPreset[]) => {
    setPresets(newPresets)
    localStorage.setItem('imagePromptPresets', JSON.stringify(newPresets))
  }
  
  const handleSavePreset = () => {
    if (!presetName.trim()) return
    
    const newPreset: UserPreset = {
      id: crypto.randomUUID(),
      name: presetName,
      description: presetDescription,
      settings: currentSettings,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }
    
    savePresets([...presets, newPreset])
    setShowSaveForm(false)
    setPresetName('')
    setPresetDescription('')
  }
  
  const handleLoadPreset = () => {
    const preset = presets.find(p => p.id === selectedPresetId)
    if (preset) {
      onLoadPreset(preset.settings)
    }
  }
  
  const handleDeletePreset = () => {
    if (!selectedPresetId) return
    if (!confirm('このプリセットを削除しますか？')) return
    
    savePresets(presets.filter(p => p.id !== selectedPresetId))
    setSelectedPresetId('')
  }
  
  const handleExportPresets = () => {
    const dataStr = JSON.stringify(presets, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = `image-prompt-presets-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    URL.revokeObjectURL(url)
  }
  
  const handleImportPresets = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    try {
      const text = await file.text()
      const data = JSON.parse(text)
      
      if (!Array.isArray(data)) {
        throw new Error('インポートデータが正しい形式ではありません')
      }
      
      // バリデーション
      for (const preset of data) {
        if (!preset.id || !preset.name || !preset.settings) {
          throw new Error('プリセットデータが不完全です')
        }
      }
      
      savePresets([...presets, ...data])
      setImportError(null)
    } catch (error) {
      setImportError(error instanceof Error ? error.message : 'インポートに失敗しました')
    }
  }
  
  return (
    <>
      <div className="card">
        <div className="p-6">
          <h3 className="text-lg font-semibold">プリセット管理</h3>
        </div>
        <div className="px-6 pb-6 space-y-4">
          <div className="space-y-2">
            <Label>保存済みプリセット ({presets.length}個)</Label>
            {presets.length > 0 ? (
              <Select value={selectedPresetId} onValueChange={setSelectedPresetId}>
                <SelectTrigger>
                  <SelectValue placeholder="プリセットを選択" />
                </SelectTrigger>
                <SelectContent>
                  {presets.map(preset => (
                    <SelectItem key={preset.id} value={preset.id}>
                      <div>
                        <div className="font-medium">{preset.name}</div>
                        {preset.description && (
                          <div className="text-xs text-muted-foreground">
                            {preset.description}
                          </div>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground">
                保存済みのプリセットはありません
              </p>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleLoadPreset}
              disabled={!selectedPresetId}
            >
              ロード
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowSaveForm(!showSaveForm)}
            >
              <Save className="h-4 w-4 mr-1" />
              現在の設定を保存
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDeletePreset}
              disabled={!selectedPresetId}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              削除
            </Button>
          </div>
          
          {/* プリセット保存フォーム */}
          {showSaveForm && (
            <div className="card">
              <div className="pt-4 space-y-4">
                <div className="space-y-2">
                  <Label>プリセット名 *</Label>
                  <Input
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="例: ファンタジー少女設定"
                  />
                </div>
                <div className="space-y-2">
                  <Label>説明（任意）</Label>
                  <Textarea
                    value={presetDescription}
                    onChange={(e) => setPresetDescription(e.target.value)}
                    placeholder="このプリセットの説明を入力"
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSavePreset}
                    disabled={!presetName.trim()}
                  >
                    保存
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowSaveForm(false)
                      setPresetName('')
                      setPresetDescription('')
                    }}
                  >
                    キャンセル
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <div className="border-t pt-4">
            <div className="flex gap-2">
              <input
                id="import-presets"
                type="file"
                accept=".json"
                onChange={handleImportPresets}
                className="hidden"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => document.getElementById('import-presets')?.click()}
              >
                <Upload className="h-4 w-4 mr-1" />
                インポート
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportPresets}
                disabled={presets.length === 0}
              >
                <Download className="h-4 w-4 mr-1" />
                エクスポート
              </Button>
            </div>
          </div>
          
          {importError && (
            <Alert variant="destructive">
              <AlertDescription>{importError}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
      
      {/* プリセット詳細表示 */}
      {selectedPresetId && presets.find(p => p.id === selectedPresetId) && (
        <div className="card">
          <div className="p-6">
            <h3 className="text-lg font-semibold">プリセット詳細</h3>
          </div>
          <div className="px-6 pb-6">
            {(() => {
              const preset = presets.find(p => p.id === selectedPresetId)!
              const settings = preset.settings
              
              return (
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">名前:</span> {preset.name}
                  </div>
                  {preset.description && (
                    <div>
                      <span className="font-medium">説明:</span> {preset.description}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">作成日:</span>{' '}
                    {new Date(preset.createdAt).toLocaleDateString('ja-JP')}
                  </div>
                  
                  <div className="border-t pt-2 mt-2">
                    <div className="font-medium mb-1">設定内容:</div>
                    {settings.gender && (
                      <div>性別: {settings.gender}</div>
                    )}
                    {settings.ageGroup && (
                      <div>年齢層: {settings.ageGroup}</div>
                    )}
                    {settings.bodyType && (
                      <div>体型: {settings.bodyType}</div>
                    )}
                    {settings.expression && (
                      <div>表情: {settings.expression}</div>
                    )}
                    {settings.hair && (
                      <>
                        {settings.hair.front && <div>前髪: {settings.hair.front}</div>}
                        {settings.hair.style && <div>髪型: {settings.hair.style}</div>}
                        {settings.hair.texture && <div>髪質: {settings.hair.texture}</div>}
                        {settings.hair.color && <div>髪色: {settings.hair.color}</div>}
                        {settings.hair.colorEffect && <div>色効果: {settings.hair.colorEffect}</div>}
                      </>
                    )}
                    {settings.eyes && (
                      <>
                        {settings.eyes.shape && <div>目の形: {settings.eyes.shape}</div>}
                        {settings.eyes.size && <div>目の大きさ: {settings.eyes.size}</div>}
                        {settings.eyes.color && <div>目の色: {settings.eyes.color}</div>}
                      </>
                    )}
                    {settings.clothing && (
                      <>
                        {settings.clothing.tops && (
                          <div>
                            トップス: {settings.clothing.topsColor
                              ? `${settings.clothing.topsColor}の${settings.clothing.tops}`
                              : settings.clothing.tops}
                          </div>
                        )}
                        {settings.clothing.bottoms && (
                          <div>
                            ボトムス: {settings.clothing.bottomsColor
                              ? `${settings.clothing.bottomsColor}の${settings.clothing.bottoms}`
                              : settings.clothing.bottoms}
                          </div>
                        )}
                        {settings.clothing.shoes && (
                          <div>
                            靴: {settings.clothing.shoesColor
                              ? `${settings.clothing.shoesColor}の${settings.clothing.shoes}`
                              : settings.clothing.shoes}
                          </div>
                        )}
                        {settings.clothing.accessories && settings.clothing.accessories.length > 0 && (
                          <div>アクセサリー: {settings.clothing.accessories.join(', ')}</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </>
  )
}